import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../constants/api';

// Create axios instance with default config
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're currently refreshing the token
let isRefreshing = false;
// Store pending requests that should be retried after token refresh
let refreshSubscribers: ((token: string) => void)[] = [];

// Function to refresh the token without using authService directly
export async function refreshAuthToken() {
  try {
    // Direct API call to refresh endpoint to avoid circular dependency
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      console.warn('No refresh token available - skipping refresh attempt');
      // Dispatch token expired event
      window.dispatchEvent(new Event('tokenExpired'));
      // Instead of throwing and redirecting, just return null to indicate no token
      // This prevents redirect loops for public pages
      return null;
    }
    
    console.log('Attempting to refresh token...');
    
    // Use axios directly instead of apiClient to avoid circular dependency
    const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, { refreshToken }, {
      headers: { 
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      }
    });
    
    if (!response.data.success) {
      console.error('Token refresh response unsuccessful:', response.data);
      // Dispatch token expired event
      window.dispatchEvent(new Event('tokenExpired'));
      throw new Error(response.data.message || 'Token refresh failed');
    }
    
    console.log('Token refresh successful');
    
    // Update stored tokens
    localStorage.setItem('token', response.data.data.accessToken);
    localStorage.setItem('refreshToken', response.data.data.refreshToken);
    
    // Dispatch an event to notify the app that tokens have been refreshed
    window.dispatchEvent(new CustomEvent('tokenRefreshed', { 
      detail: { accessToken: response.data.data.accessToken }
    }));
    
    // Return the new access token
    return response.data.data.accessToken;
  } catch (error) {
    console.error('Token refresh failed:', error);
    // Clear tokens
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    
    // Dispatch token expired event instead of redirecting directly
    // This allows the AuthContext to handle the logout flow consistently
    window.dispatchEvent(new Event('tokenExpired'));
    
    throw error;
  }
}

// Function to retry failed requests with new token
export function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
}

// Add token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    
    // Add timestamp to prevent caching issues with token
    const url = config.url;
    if (url && !url.includes('refresh')) {
      const separator = url.includes('?') ? '&' : '?';
      config.url = `${url}${separator}_t=${Date.now()}`;
    }
  }
  return config;
});

// Handle 401 responses
apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Check if the error is token-related (401 Unauthorized)
    const isTokenError = error.response?.status === 401;
    
    // Check if the error message indicates token expiration
    const isExpiredToken = 
      error.response?.data && 
      typeof error.response?.data === 'object' && 
      ((error.response?.data as any).message?.includes('expired') || 
      (error.response?.data as any).error?.includes('IDX10223'));
    
    // Log the error for debugging
    if (isTokenError || isExpiredToken) {
      console.log('Token error detected:', {
        status: error.response?.status,
        isExpiredToken,
        url: originalRequest.url,
        hasRetried: originalRequest._retry
      });
    }
    
    // If error is 401 or token expired and we haven't already tried to refresh
    if ((isTokenError || isExpiredToken) && !originalRequest._retry) {
      console.log('Attempting to recover from token error');
      
      if (isRefreshing) {
        console.log('Token refresh already in progress, waiting for completion');
        // If we're already refreshing, wait for the new token
        try {
          const token = await new Promise<string>((resolve, reject) => {
            // Add timeout to prevent infinite waiting
            const timeoutId = setTimeout(() => {
              reject(new Error('Token refresh timeout'));
            }, 10000); // 10 second timeout
            
            refreshSubscribers.push(newToken => {
              clearTimeout(timeoutId);
              if (newToken) {
                resolve(newToken);
              } else {
                reject(new Error('Received null token from refresh'));
              }
            });
          });
          
          // Retry the original request with new token
          console.log('Received refreshed token, retrying request');
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        } catch (err) {
          console.error('Error waiting for token refresh:', err);
          return Promise.reject(err);
        }
      }
      
      // Start refreshing process
      console.log('Starting token refresh process');
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        const token = await refreshAuthToken();
        
        if (!token) {
          console.error('Refresh token returned null');
          // The refreshAuthToken function will handle dispatching the tokenExpired event
          throw new Error('Failed to refresh token');
        }
        
        // Notify all subscribers that token is refreshed
        console.log('Token refreshed successfully, notifying subscribers');
        onTokenRefreshed(token);
        
        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axios(originalRequest);
      } catch (err) {
        console.error('Token refresh failed in interceptor:', err);
        // Clear subscribers to prevent memory leaks
        refreshSubscribers = [];
        
        // Note: We don't need to dispatch tokenExpired here as refreshAuthToken already does it
        
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;
